import React, { useState } from "react";
import {
  TextField,
  Button,
  Box,
  InputAdornment,
  IconButton,
  CircularProgress,
  Alert,
  Typography,
  Collapse,
  Card,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import axios from "axios";
import CloseIcon from "@mui/icons-material/Close";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setAuth } from "../../../app-store/authSlice";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const payload = { email, password };
      const { data, status } = await axios.post(
        "https://le-travaille-server.onrender.com/auth/login",
        payload,
      );

      if (status === 200) {
        sessionStorage.setItem("user id", data.user.userId);
        dispatch(setAuth({ token: data.token, userId: data.user.userId }));
        navigate("/user/dashboard");
      }
    } catch (err) {
      console.log(err);
      setError(err.response?.data?.message || "Login failed");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
        px: { xs: 0.5, sm: 2 },
        py: 2,
      }}
    >
      <Card
        sx={{
          maxWidth: 1100,
          width: "100%",
          minHeight: 550,
          borderRadius: { xs: 2, md: 4 },
          overflow: "hidden",
          boxShadow: "0 24px 64px rgba(0,0,0,0.15)",
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
        }}
      >
        {/* Left Column - Image */}
        <Box
          sx={{
            flex: 1.1,
            minHeight: isMobile ? 250 : "auto",
            backgroundImage: `linear-gradient(135deg, rgba(0, 120, 111, 0.85), rgba(0, 120, 111, 0.95)), url('https://i.ibb.co/mh7GRRf/Man-Searching-for-a-Job-removebg.png')`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            padding: { xs: 3, md: 5 },
            color: "white",
            position: "relative",
          }}
        >
          {/* Decorative elements */}
          <Box
            sx={{
              position: "absolute",
              top: 30,
              right: 30,
              width: 80,
              height: 80,
              borderRadius: "50%",
              backgroundColor: "rgba(255, 215, 0, 0.08)",
              border: "2px solid rgba(255, 215, 0, 0.15)",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              bottom: 50,
              left: 40,
              width: 50,
              height: 50,
              borderRadius: "50%",
              backgroundColor: "rgba(255, 215, 0, 0.06)",
              border: "2px solid rgba(255, 215, 0, 0.1)",
            }}
          />

          <Typography
            variant="h2"
            sx={{
              fontWeight: 700,
              color: "#FFD700",
              mb: 2,
              zIndex: 1,
              textAlign: "center",
              fontSize: { xs: "2.5rem", md: "3.5rem" },
            }}
          >
            Le-Travaille
          </Typography>
          <Typography
            variant="h6"
            sx={{
              opacity: 0.9,
              textAlign: "center",
              zIndex: 1,
              maxWidth: 350,
              fontWeight: 400,
              fontSize: { xs: "1rem", md: "1.25rem" },
            }}
          >
            Find your dream job and connect with top employers
          </Typography>
          <Box
            sx={{
              display: "flex",
              gap: 2,
              mt: 4,
              zIndex: 1,
            }}
          >
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                backgroundColor: "#FFD700",
                opacity: 0.6,
              }}
            />
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                backgroundColor: "#FFD700",
                opacity: 0.3,
              }}
            />
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                backgroundColor: "#FFD700",
                opacity: 0.15,
              }}
            />
          </Box>
        </Box>

        {/* Right Column - Form */}
        <Box
          sx={{
            flex: 1.4,
            padding: { xs: 2, sm: 3, md: 5 },
            backgroundColor: "#fff",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            minHeight: 450,
          }}
        >
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              color: "#00786F",
              textAlign: "center",
              mb: 1,
              fontSize: { xs: "1.75rem", md: "2.25rem" },
            }}
          >
            Welcome Back!
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "#666",
              mb: 3,
              textAlign: "center",
              fontSize: "1rem",
            }}
          >
            Don't have an account?{" "}
            <span
              onClick={() => (window.location.href = "/auth/signup")}
              style={{
                color: "#00786F",
                cursor: "pointer",
                fontWeight: 600,
                textDecoration: "none",
              }}
            >
              Sign Up
            </span>
          </Typography>

          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              label="Email Address"
              variant="outlined"
              fullWidth
              margin="normal"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              type="email"
              required
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  height: { xs: "52px", sm: "56px" },
                  backgroundColor: "#f8f9fa",
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    backgroundColor: "#f1f3f5",
                    transform: "scale(1.01)",
                    boxShadow: "0 2px 8px rgba(0, 120, 111, 0.08)",
                  },
                  "&.Mui-focused": {
                    backgroundColor: "#fff",
                    transform: "scale(0.99)",
                    boxShadow: "0 4px 16px rgba(0, 120, 111, 0.15)",
                  },
                  "&:active": {
                    transform: "scale(0.98)",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#00786F",
                    borderWidth: 2,
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#00786F",
                    borderWidth: 2,
                  },
                },
                "& .MuiInputLabel-root": {
                  fontSize: { xs: "0.9rem", sm: "0.95rem" },
                  color: "#1A2332",
                  fontWeight: 500,
                  "&.Mui-focused": {
                    color: "#00786F",
                  },
                },
                "& .MuiInputBase-input": {
                  fontSize: "1rem",
                  padding: { xs: "14px 14px", sm: "16px 14px" },
                },
                mb: 2,
              }}
            />

            <TextField
              label="Password"
              variant="outlined"
              fullWidth
              margin="normal"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              type={showPassword ? "text" : "password"}
              required
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      sx={{
                        "&:hover": {
                          backgroundColor: "rgba(0, 120, 111, 0.04)",
                        },
                      }}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  height: { xs: "52px", sm: "56px" },
                  backgroundColor: "#f8f9fa",
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    backgroundColor: "#f1f3f5",
                    transform: "scale(1.01)",
                    boxShadow: "0 2px 8px rgba(0, 120, 111, 0.08)",
                  },
                  "&.Mui-focused": {
                    backgroundColor: "#fff",
                    transform: "scale(0.99)",
                    boxShadow: "0 4px 16px rgba(0, 120, 111, 0.15)",
                  },
                  "&:active": {
                    transform: "scale(0.98)",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#00786F",
                    borderWidth: 2,
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#00786F",
                    borderWidth: 2,
                  },
                },
                "& .MuiInputLabel-root": {
                  fontSize: { xs: "0.9rem", sm: "0.95rem" },
                  color: "#1A2332",
                  fontWeight: 500,
                  "&.Mui-focused": {
                    color: "#00786F",
                  },
                },
                "& .MuiInputBase-input": {
                  fontSize: "1rem",
                  padding: { xs: "14px 14px", sm: "16px 14px" },
                },
                mb: 1,
              }}
            />

            <Typography
              variant="body2"
              align="right"
              sx={{
                mb: 3,
                mt: 1,
              }}
            >
              <span
                onClick={() => (window.location.href = "/auth/forgot-password")}
                style={{
                  color: "#00786F",
                  cursor: "pointer",
                  fontWeight: 500,
                  textDecoration: "none",
                }}
              >
                Forgot Password?
              </span>
            </Typography>

            {error && (
              <Collapse in={Boolean(error)}>
                <Alert
                  severity="error"
                  action={
                    <IconButton
                      aria-label="close"
                      color="inherit"
                      size="small"
                      onClick={() => setError("")}
                    >
                      <CloseIcon fontSize="inherit" />
                    </IconButton>
                  }
                  sx={{ mb: 2, borderRadius: 2 }}
                >
                  {error}
                </Alert>
              </Collapse>
            )}

            <Button
              type="submit"
              variant="contained"
              fullWidth
              sx={{
                backgroundColor: "#00786F",
                color: "#fff",
                fontWeight: 600,
                py: { xs: 1.5, sm: 1.8 },
                borderRadius: 2,
                textTransform: "none",
                fontSize: { xs: "0.95rem", sm: "1.1rem" },
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  backgroundColor: "#005b54",
                  transform: "scale(1.02)",
                  boxShadow: "0 4px 16px rgba(0, 120, 111, 0.3)",
                },
                "&:active": {
                  transform: "scale(0.98)",
                },
                "&:disabled": {
                  backgroundColor: "#00786F",
                  opacity: 0.6,
                  transform: "none",
                  boxShadow: "none",
                },
              }}
              disabled={isLoading}
            >
              {isLoading ? (
                <CircularProgress size={28} sx={{ color: "#fff" }} />
              ) : (
                "Login"
              )}
            </Button>
          </Box>
        </Box>
      </Card>
    </Box>
  );
};

export default Login;
